/**
 * D51e Phase 3 — the Digest dashboard's client-authored copy deck.
 *
 * Every member-facing string the Digest modules introduce that is NOT one
 * of the server's own `copy.headline`/`copy.body`/`copy.closing` fields
 * lives here, following the module-per-feature-domain convention started
 * by `onboardingGate.ts`. Server `copy` fields render verbatim wherever
 * they appear — nothing here duplicates or overrides them.
 *
 * Binding constraints (D51e plan, Phase 3):
 * - An issue, never an edition — anywhere, including the cadence tag below.
 * - No department bylines anywhere (no server field sources one, so none
 *   is stubbed client-side either).
 * - "TableMatch" appears nowhere — the only pairing vocabulary is
 *   TasteMatch™, per FR-E22's existing "you and Bruce share a high
 *   TasteMatch with Veselka" pattern.
 */

// ============================================================================
// Cadence tag — the visible "Daily issue" / "Weekly issue" tag on an All
// Issues archive row (D51f), and the vocabulary the dateline surface will
// reuse. Kept here so taste-curator can grep it alongside everything else in
// this file.
// ============================================================================

export const DIGEST_CADENCE_TAGS: Record<'daily' | 'weekly', string> = {
  daily: 'Daily issue',
  weekly: 'Weekly issue',
};

// ============================================================================
// Module department headings — every module's `<h3>` (or, for Cover, the
// sr-only landmark/fallback text) migrated here from a hardcoded literal
// in its own file, per this project's convention that every member-facing
// string lives in `src/copy/` (D51e review round 2, Finding 13).
// ============================================================================

export const COVER_EYEBROW_LABEL = 'Cover';
export const COVER_FALLBACK_HEADLINE = 'This issue';
export const LOCAL_MATCHES_HEADING = 'Local Matches';
export const WORTH_THE_TRIP_HEADING = 'Worth the Trip';
export const DUE_FOR_A_RETURN_HEADING = 'Due for a Return';
export const BRING_A_FRIEND_HEADING = 'Bring a Friend';
export const FRIEND_ACTIVITY_HEADING = 'Friend Activity';
export const TRENDING_LISTS_HEADING = 'Trending Lists';
export const FEATURED_LISTS_HEADING = 'Featured Lists';
export const TASTE_OF_THE_MONTH_HEADING = 'Taste of the Month';
export const BACK_PAGE_HEADING = 'Back Page';
export const PREVIOUS_ISSUES_HEADING = 'Previous Issues';

// ============================================================================
// Provenance labels — migrated here from Phase 2's inline literals
// (`DigestRestaurantLead.tsx`, `DigestRestaurantRow.tsx`, `ConsensusPill.tsx`)
// so every module reads the same constant rather than a re-typed copy.
// ============================================================================

export const RATED_BY_YOU_LABEL = 'Rated by you';

/** Verbatim per `copy/filterBar.ts`'s `tierSortVocabulary.public_consensus.label` (CD, 2026-09-24). */
export const PUBLIC_OPINION_LABEL = 'Public Opinion';

// ============================================================================
// Bring a Friend — pair vocabulary (FR-E22's existing pattern, no new term)
// ============================================================================

/**
 * "You and {friend} share a {tier} TasteMatch™" — the existing vocabulary
 * FR-E22 already establishes ("you and Bruce share a high TasteMatch with
 * Veselka"), applied here without a restaurant (Bring a Friend's
 * `restaurantId` is structurally always null). `tierLabel` is whatever
 * `getCompatibilityEmoji` already returns for the pair's converted rating
 * (e.g. "High", "Medium") — never a re-typed tier word.
 */
export function bringAFriendPairSentence(friendName: string, tierLabel: string): string {
  return `You and ${friendName} share a ${tierLabel} TasteMatch™`;
}

// ============================================================================
// Friend-count placeholder actions (PlaceholderCard's `social` family)
// ============================================================================

export const FIND_FRIENDS_ACTION = 'Find friends';
export const INVITE_A_FRIEND_ACTION = 'Invite a friend';

/** 0 friends -> find; 1+ friends -> invite. The only two `social`-family action strings. */
export function socialPlaceholderAction(friendCount: number): string {
  return friendCount <= 0 ? FIND_FRIENDS_ACTION : INVITE_A_FRIEND_ACTION;
}

// ============================================================================
// List-rank modules (Trending Lists, Featured Lists) — attribution line
// ============================================================================

/** "by {ownerName} · N restaurant(s)" — the list-rank modules' owner/count attribution line. */
export function listAttributionLine(ownerName: string, restaurantCount: number): string {
  return `by ${ownerName} · ${restaurantCount} ${restaurantCount === 1 ? 'restaurant' : 'restaurants'}`;
}

// ============================================================================
// Friend Activity — public-action vocabulary (never ratings/scores, TIP4/C3)
// ============================================================================

/**
 * Client-side allowlist, mirroring `friend_activity.py`'s
 * `PUBLIC_ACTIVITY_TYPES` exactly (`checked_in`, `list_made_public`,
 * `list_saved`, `list_copied`, `list_updated`) — deliberately no rating
 * activity type, and no default/catch-all branch anywhere this is
 * consumed. An unrecognized or null `activityType` is dropped, not
 * rendered with a fallback label.
 */
export const FRIEND_ACTIVITY_TYPES = [
  'checked_in',
  'list_made_public',
  'list_saved',
  'list_copied',
  'list_updated',
] as const;

export type FriendActivityType = (typeof FRIEND_ACTIVITY_TYPES)[number];

export function isFriendActivityType(value: string | null | undefined): value is FriendActivityType {
  return value != null && (FRIEND_ACTIVITY_TYPES as readonly string[]).includes(value);
}

/**
 * One sentence per public activity type. `subjectName` is a restaurant name
 * for `checked_in`, a list name for the four `list_*` types — never a
 * rating, a score, or anything TIP4/C3 reserves to the member's own issue.
 */
export function friendActivitySentence(activityType: FriendActivityType, friendName: string, subjectName: string): string {
  switch (activityType) {
    case 'checked_in':
      return `${friendName} checked in at ${subjectName}`;
    case 'list_made_public':
      return `${friendName} made "${subjectName}" public`;
    case 'list_saved':
      return `${friendName} saved "${subjectName}"`;
    case 'list_copied':
      return `${friendName} copied "${subjectName}"`;
    case 'list_updated':
      return `${friendName} updated "${subjectName}"`;
  }
}

// ============================================================================
// Going to press (D51e Phase 4) — the calm, uniform not-ready state. Renders
// identically across every dispatch/refusal outcome (200/202/202/400/429/503)
// — no status, retry count, or posture detail belongs in any string here.
// ============================================================================

export const GOING_TO_PRESS_HEADLINE = 'Preparing your issue…';
export const GOING_TO_PRESS_BODY = "We're putting your next issue together. Check back shortly.";

// ============================================================================
// Sidebar extras (D51e Phase 4) — Feed teaser and Refine Your Taste are
// deliberately not among the eleven module keys (design notes §5): plain
// `<h3>` headings, no dept-label eyebrow, no dashed-border placeholder even
// when empty — their empty state uses the same solid card shell as populated.
// ============================================================================

export const FEED_TEASER_HEADING = 'From your friends';
export const FEED_TEASER_EMPTY = 'Nothing new from your circle yet.';
export const FEED_TEASER_LINK = 'See your feed';

export const REFINE_YOUR_TASTE_HEADING = 'Refine your taste';
export const REFINE_YOUR_TASTE_EMPTY = "You've completed every quiz — thank you. We'll let you know when a new one arrives.";
export const REFINE_YOUR_TASTE_LINK = 'Continue';

/**
 * "{friendName} {verb}" — built from the feed's own `activityTypeDisplay`, mirroring `ActivityCard.tsx`'s
 * fallback-text pattern (`activityTypeDisplay?.toLowerCase() || 'had an activity'`). Never a re-typed vocabulary.
 * The server omits `activityTypeDisplay` on plain `activity` feed items (only grouped activities carry it),
 * so a missing or empty value falls back to the same neutral wording `ActivityCard` uses.
 */
export function feedTeaserSentence(friendName: string, activityTypeDisplay?: string | null): string {
  return `${friendName} ${activityTypeDisplay?.toLowerCase() || 'had an activity'}`;
}

// ============================================================================
// Archive + past-issue surfaces (D51f Phase 4) — Previous Issues tap target,
// All Issues, and the past-issue view. "Issue" vocabulary throughout, never
// "edition"; nothing here nags — a locked row is an invitation, not a wall.
// ============================================================================

/** Previous Issues strip's link to the full archive. Only rendered when the strip has at least one issue. */
export const SEE_ALL_ISSUES_LINK = 'See all issues';

/** The All Issues screen's page title (its `h1`). */
export const ALL_ISSUES_HEADING = 'All Issues';

/**
 * Back-link used on every past-issue / archive surface; always points at `/dashboard`.
 * Reads "your latest issue", not "today's issue": free (weekly) members' current
 * issue can be up to six days old, so "today's" reads as false on these surfaces.
 */
export const BACK_TO_TODAYS_ISSUE = 'Back to your latest issue';

/**
 * Mirror of the SERVER's Previous Issues placeholder body
 * (`paire_api/services/digest/template_copy.py`, `_PLACEHOLDER_BODY_OVERRIDES['previous_issues']`).
 * The archive screen has no slot to read it from, so it repeats the literal
 * for a member who reaches it directly with zero issues. If the server
 * copy changes, change this with it.
 */
export const ARCHIVE_STARTS_TODAY_BODY = 'Your archive starts today.';

/**
 * One of two snapshot-copy exceptions to "server copy renders verbatim": inside a
 * past-issue view the server's "Your archive starts today." would point at
 * the viewing moment rather than the issue's own date, so the oldest issue's
 * empty Previous Issues strip says this instead. The Dashboard's live strip
 * is unaffected.
 */
export const PREVIOUS_ISSUES_SNAPSHOT_EMPTY_BODY = 'This was your first issue — nothing earlier to show.';

/**
 * The other snapshot-copy exception: inside a past-issue view, the server sent
 * earlier entries but NONE resolves locally (unreadable or dangling ids).
 * Both of the server's own strings are false there ("N earlier issues are
 * waiting for you" — none can be shown; "starts today" — it did not), and so
 * is the "first issue" line above, so this says only what is true.
 */
export const PREVIOUS_ISSUES_SNAPSHOT_UNRESOLVED_BODY = "Earlier issues aren't available to show here.";

/**
 * Accessible subject for an archive/strip row: "{cover}, issue from {date}"
 * plus, on an archive row, the visible cadence tag ("…, Weekly issue") so the
 * row's visible text is contained in its accessible name (WCAG 2.5.3). The
 * lock suffix is appended by `LockedFeature`.
 */
export function issueRowLabel(dateText: string, coverName: string | null | undefined, cadenceTag?: string | null): string {
  const subject = coverName ? `${coverName}, issue from ${dateText}` : `Issue from ${dateText}`;
  return cadenceTag ? `${subject}, ${cadenceTag}` : subject;
}

// --- All Issues screen ---
export const ARCHIVE_LOADING_LABEL = 'Loading your issues';
export const ARCHIVE_LOAD_MORE = 'Show more issues';
export const ARCHIVE_LOADING_MORE_LABEL = 'Loading more issues';
export const ARCHIVE_ERROR_HEADLINE = "We couldn't load your issues";
export const ARCHIVE_ERROR_BODY = 'Check your connection, or try again in a moment.';
export const ARCHIVE_LOAD_MORE_ERROR = "We couldn't load more issues.";
export const RETRY_LABEL = 'Try again';
export const RETRYING_LABEL = 'Trying again…';

// --- Past-issue view ---
/** The past-issue screen's page title (its `h1`), constant across its loading/ready/locked/not-found/error states. */
export const PAST_ISSUE_HEADING = 'Earlier Issue';
export const PAST_ISSUE_LOADING_LABEL = 'Loading this issue';

/** The dated snapshot banner: "From your {date} issue". */
export function pastIssueBannerText(dateText: string): string {
  return `From your ${dateText} issue`;
}

export const PAST_ISSUE_LOCKED_HEADLINE = 'Earlier issues come with Paire+';
export const PAST_ISSUE_LOCKED_BODY = 'Your latest issue is always yours. Every earlier one is saved and ready whenever you want it.';
export const PAST_ISSUE_LOCKED_CTA = 'Unlock with Paire+';

export const ISSUE_NOT_FOUND_HEADLINE = "This issue isn't available";
export const ISSUE_NOT_FOUND_BODY = "We couldn't find that issue. The link may not be quite right.";

export const ISSUE_ERROR_HEADLINE = "We couldn't open this issue";
/** The same line as the archive's: a failure to open one issue and a failure to list them read alike. */
export const ISSUE_ERROR_BODY = ARCHIVE_ERROR_BODY;

// ============================================================================
// Weekly-issue upsell (D51f Phase 5, FR-P7a / FR-P13) — the masthead's quiet
// link and the one closing card, both for free (weekly) members only and both
// outside the eleven-module reading flow. Invitations, never nags (FR-U3); the
// pitch is FREQUENCY, never depth — a weekly issue is complete (FR-P2), so
// nothing here implies it is not. "Every morning" is the spec's own wording
// (FR-P13) and the same promise `UPGRADE_PROMPT_COPY.dailyCadence` makes.
// The cadence tag itself is `DIGEST_CADENCE_TAGS` above.
// ============================================================================

/** The masthead's quiet text link, verbatim FR-P13. Opens the `dailyCadence` upgrade prompt. */
export const MASTHEAD_UPSELL_LINK = 'Get it every morning with Paire+';

/** The closing card: one line about frequency, one supporting line, one invitation. */
export const CLOSING_CARD_HEADLINE = 'Want your issue more often?';
export const CLOSING_CARD_BODY = 'With Paire+, a new issue is waiting for you every morning, instead of once a week.';
/** Deliberately NOT the default "Upgrade to Paire+" — the card invites rather than sells. */
export const CLOSING_CARD_CTA = 'Get it every morning';

// ============================================================================
// Upgrade prompt chrome for the two Digest contexts (D51f Phase 5, taste-curator
// note on the shared `UpgradePromptModal`). The four legacy contexts keep the
// modal's original "<cta> to unlock" line and generic feature list untouched;
// `dailyCadence` and `issueArchive` lead with what the member gets instead.
// ============================================================================

/** Sub-heading under the modal title for the two Digest contexts (replaces "Upgrade to Paire+ to unlock"). */
export const upgradeModalDigestSubtitle = (tierName: string): string => `Included with ${tierName}`;

/** One extra line at the TOP of the modal's feature list, for those two contexts only. */
export const UPGRADE_MODAL_DIGEST_FEATURE = {
  dailyCadence: 'A new issue every morning',
  issueArchive: 'Every past issue, saved for you',
} as const;
